import type { ApiResponse } from '../../types'
import type {
  ReportDto,
  CreateReportRequest,
  KudosEligibilityDto,
  CreateKudosRequest,
  BlockDto,
  CreateBlockRequest,
} from '../../types/community'
import { client, unwrap, apiError, CotApiError } from './client'

// COTC_SafetyKudos — real bodies over the shared community axios instance.

async function run<T>(op: () => Promise<ApiResponse<T>>): Promise<T> {
  try {
    return unwrap(await op())
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** POST /reports — file a report (never blocked, any supervision level). */
export async function createReport(req: CreateReportRequest): Promise<ReportDto> {
  return run(async () => (await client.post<ApiResponse<ReportDto>>('/reports', req)).data)
}

/** GET /reports — the reporter family's own view (status + outcome). */
export async function listReports(): Promise<ReportDto[]> {
  return run(async () => (await client.get<ApiResponse<ReportDto[]>>('/reports')).data)
}

/** POST /reports/{id}/attachments — multipart, ≤4, ≤8MB each. */
export async function addReportAttachment(id: string, file: File): Promise<void> {
  const form = new FormData()
  form.append('file', file)
  await run(async () =>
    (await client.post<ApiResponse>(`/reports/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data,
  )
}

/** POST /kudos — public, permanent, one per person per six months. */
export async function createKudos(req: CreateKudosRequest): Promise<void> {
  await run(async () => (await client.post<ApiResponse>('/kudos', req)).data)
}

/** GET /kudos/eligibility — modal-open precheck. */
export async function getKudosEligibility(
  targetPersonId?: string,
  targetHandle?: string,
): Promise<KudosEligibilityDto> {
  return run(async () =>
    (await client.get<ApiResponse<KudosEligibilityDto>>('/kudos/eligibility', {
      params: { targetPersonId, targetHandle },
    })).data,
  )
}

/** POST /blocks — silent block (blocker's world filters; blocked never told). */
export async function createBlock(req: CreateBlockRequest): Promise<BlockDto> {
  return run(async () => (await client.post<ApiResponse<BlockDto>>('/blocks', req)).data)
}

/** GET /blocks — the family's block list. */
export async function listBlocks(): Promise<BlockDto[]> {
  return run(async () => (await client.get<ApiResponse<BlockDto[]>>('/blocks')).data)
}

/** DELETE /blocks/{id} — unblock. */
export async function removeBlock(id: string): Promise<void> {
  await run(async () => (await client.delete<ApiResponse>(`/blocks/${id}`)).data)
}
